import { MAX_FLIGHT_TICKS, MAX_PROJECTILE_REACH, REACT_TICKS, flightTicksAt, walkOutTicks } from "../combat/projectile.ts";
import { identifier, report, ticks, whole, type Place } from "./checks.ts";
import type { Ability, ProjectileShape, RulesProblem } from "./types.ts";

/**
 * The projectile shape's checks (ADR-095 decision 2): its seven fields within their ranges, the flight at full range, the
 * dodge check and the reach, read with the shape; the cooldown and the combo, read with the ability; and, across the
 * tables, that no weapon names a projectile, so no character's bar holds one until a player's spells lift it (decision 22).
 */

const MIN_RANGE = 16;
const MAX_RANGE = 128;
const MAX_SPEED = 64;
const MIN_RADIUS = 4;
const MAX_RADIUS = 32;
const MAX_PROJECTILE_TARGETS = 8;
const MAX_RELEASE_TICKS = 255;

/**
 * Checks a projectile shape's fields and the checks that read the shape alone: the flight at full range at most
 * `MAX_FLIGHT_TICKS`, and the range and the radius together at most `MAX_PROJECTILE_REACH`. The dodge check reads the
 * walk's speed from the constants and is `checkProjectileDodge`'s.
 *
 * @param place Where to report.
 * @param shape The shape's fields.
 * @param exact Whether it holds exactly the projectile's fields.
 * @returns The shape, or null.
 */
export function projectileShapeOf(place: Place, shape: Readonly<Record<string, unknown>>, exact: boolean): ProjectileShape | null {
  const range = whole(place, shape["rangeSixteenths"], "shape.rangeSixteenths", MIN_RANGE, MAX_RANGE);
  const minRange = whole(place, shape["minRangeSixteenths"], "shape.minRangeSixteenths", 0, range === null ? MAX_RANGE - 1 : range - 1);
  const speed = whole(place, shape["speedSixteenths"], "shape.speedSixteenths", 1, MAX_SPEED);
  const radius = whole(place, shape["radiusSixteenths"], "shape.radiusSixteenths", MIN_RADIUS, MAX_RADIUS);
  const maxTargets = whole(place, shape["maxTargets"], "shape.maxTargets", 1, MAX_PROJECTILE_TARGETS);
  const releaseTicks = ticks(place, shape["releaseTicks"], "shape.releaseTicks", 1, MAX_RELEASE_TICKS);
  const look = identifier(place, shape["look"], "shape.look");
  if (range === null || minRange === null || speed === null || radius === null || maxTargets === null || releaseTicks === null || look === null || !exact) {
    return null;
  }
  let ok = true;
  const full = flightTicksAt(range, speed);
  if (full > MAX_FLIGHT_TICKS) {
    report(place, `the flight at full range is ${full} ticks (${range} sixteenths at ${speed} a tick), and it is at most ${MAX_FLIGHT_TICKS}`);
    ok = false;
  }
  if (range + radius > MAX_PROJECTILE_REACH) {
    report(place, `shape.rangeSixteenths and shape.radiusSixteenths reach ${range + radius} sixteenths, and they reach at most ${MAX_PROJECTILE_REACH}`);
    ok = false;
  }
  return ok ? { kind: "projectile", rangeSixteenths: range, minRangeSixteenths: minRange, speedSixteenths: speed, radiusSixteenths: radius, maxTargets, releaseTicks, look } : null;
}

/**
 * What a projectile ability is checked against beyond its shape: its cooldown, combo and `given`, each null or undefined
 * when it was wrong.
 */
export interface ProjectileContext {
  readonly cooldownTicks: number | null;
  readonly combo: unknown;
  readonly given: boolean | null;
}

/**
 * Checks what a projectile ability holds beside its shape: a cooldown of at least the release and the flight at full
 * range, so a caster never holds two shots pending or in flight (ADR-095 decision 5), no combo, and not given. A chain,
 * a displacement and a heal are refused by their own checks, which name the shape.
 *
 * @param place Where to report.
 * @param shape The checked shape.
 * @param ability The ability's cooldown, combo and `given`.
 * @returns True when it passes.
 */
export function projectileAbilityChecks(place: Place, shape: ProjectileShape, ability: ProjectileContext): boolean {
  let ok = true;
  const least = shape.releaseTicks + flightTicksAt(shape.rangeSixteenths, shape.speedSixteenths);
  if (ability.cooldownTicks !== null && ability.cooldownTicks < least) {
    report(place, `cooldownTicks is ${ability.cooldownTicks}, less than the release and the flight at full range, ${least}, so a caster would hold two shots`);
    ok = false;
  }
  if (ability.combo !== null && ability.combo !== undefined) {
    report(place, "a projectile plays no combo");
    ok = false;
  }
  if (ability.given === true) {
    report(place, "a projectile is given to no character: no bar holds one until a player's spells (ADR-095 decision 22)");
    ok = false;
  }
  return ok;
}

/**
 * The dodge check (ADR-095 decision 2), across the abilities and the constants: each projectile's flight at its minimum
 * range is at least the walk-out of its burst at the rules' `walk_speed` and `REACT_TICKS`, so a standing player can
 * walk out of it. Nothing is checked while `walk_speed` itself was refused, which the constants table already reports
 * (ADR-116 decision 28).
 *
 * @param abilities The abilities that passed their checks.
 * @param walkSpeed The checked `walk_speed` in speed units, or null when the constants hold none that passed.
 * @param problems Where problems go.
 */
export function checkProjectileDodge(abilities: readonly Ability[], walkSpeed: number | null, problems: RulesProblem[]): void {
  if (walkSpeed === null) {
    return;
  }
  for (const ability of abilities) {
    const shape = ability.shape;
    if (shape.kind !== "projectile") {
      continue;
    }
    const nearest = flightTicksAt(shape.minRangeSixteenths, shape.speedSixteenths);
    const walkOut = walkOutTicks(shape.radiusSixteenths, walkSpeed);
    if (nearest < walkOut + REACT_TICKS) {
      report({ table: "abilities", id: ability.id, problems }, `the flight at the minimum range is ${nearest} ticks (${shape.minRangeSixteenths} sixteenths at ${shape.speedSixteenths} a tick), less than the walk-out of ${walkOut} ticks and ${REACT_TICKS} to react, so a standing player cannot walk out of it (ADR-095 decision 2)`);
    }
  }
}

/**
 * Reports each projectile ability a weapon's blow names: no bar holds one until a player's spells (ADR-095 decision 22).
 *
 * @param abilities The abilities that passed their checks.
 * @param weaponOf Each ability a blow names and the first weapon naming it.
 * @param problems Where problems go.
 */
export function checkProjectileWeapons(abilities: readonly Ability[], weaponOf: ReadonlyMap<string, string>, problems: RulesProblem[]): void {
  for (const ability of abilities) {
    const weapon = weaponOf.get(ability.id);
    if (ability.shape.kind === "projectile" && weapon !== undefined) {
      report({ table: "abilities", id: ability.id, problems }, `the weapon ${weapon} names it, and no weapon names a projectile until a player's spells (ADR-095 decision 22)`);
    }
  }
}
